<template>
    <div class="table-responsive">
        <!-- <h5>All Records </h5> -->
        <div
            v-if="category == PHARMACY_ID"
            class="d-flex justify-content-end align-items-center mb-3"
        >
            <label class="mb-0 mr-2">Source: </label>
            <b-select
                v-model="source"
                class="form-control col-md-2"
                @change="getList()"
            >
                <option :value="null">All</option>
                <option :value="1">In House</option>
                <option v-if="vetsourceEnabled && vetcoveEnabled" :value="2">
                    Online
                </option>
                <option v-if="vetsourceEnabled" :value="3">
                    Online Vetsource
                </option>
                <option v-if="vetcoveEnabled" :value="4">Online Vetcove</option>
            </b-select>
        </div>

        <loader v-if="isLoading" />

        <table
            v-else
            id="table-medical-records"
            class="table b-table list table-hover table-borderless"
        >
            <thead>
                <tr>
                    <th>Record:</th>
                    <th>Added:</th>
                    <th width="40%">Summary:</th>
                    <th>Reference:</th>
                    <th width="10%">Provider:</th>
                    <!-- 
                        EPIC-1000 : Medical Note migration 
                        column Naming differences for soap and patient as below
                    -->
                    <th width="10%">
                        {{ isSoap ? "Price:" : "Created By:" }}
                    </th>
                    <th>{{ isSoap ? "Billing:" : "Invoice:" }}</th>
                    <th></th>
                </tr>
            </thead>
            <tbody>
                <template v-for="item in list">
                    <row-record
                        v-if="item.module === 'record'"
                        :row="item"
                        :processing="selectedRowId"
                        :key="`record${item.id}`"
                        :data-record-id="item.id"
                        :content-expanded="contentExpandedId === item.id"
                        :isNewLabProfile="isNewLab"
                        @reload="getList"
                        @update-invoice-status="updateInvoiceStatus"
                        @delete="deleteRecord"
                        @view="viewRecord"
                        @edit="editRecord"
                        @duplicate="duplicateRecord"
                        @edit-diagnosis="editDiagnosis"
                        @refill="refillRecord"
                        @print="printLabel(item)"
                        @expand="expandSummary"
                        @history="viewHistoryRecord"
                        @add-addon-test="addAddonTest"
                    />
                    <row-notes
                        v-if="item.module === 'note'"
                        :row="item"
                        :key="`note-${item.id}`"
                        :data-record-id="item.id"
                        :content-expanded="contentExpandedId === item.id"
                        @edit="editNote(item)"
                        @delete="deleteNote(item)"
                        @duplicatenote="duplicateNote(item)"
                        @edit-diagnosis="editDiagnosis"
                        @expand="expandSummary"
                        :type="'record'"
                    />
                </template>
                <tr v-if="list.length === 0">
                    <td colspan="8">
                        <i>No records available</i>
                    </td>
                </tr>
            </tbody>
        </table>
        <history-pagination
            v-if="count"
            ref="historyPagination"
            :type="'record'"
            :category="category"
            @getList="getList"
        ></history-pagination>

        <modal-delete-record
            ref="modalDeleteRecord"
            @reload="onSuccess"
            :patientId="patientId"
        />
        <SoapDelete
            v-if="isSoapEnableBeta"
            ref="modalDeleteNote"
            @reload="getList"
            @success="reloadTabCount"
        />
        <modal-delete
            v-else
            ref="modalDeleteNote"
            @reload="getList"
            @success="reloadTabCount"
        />
        <modal-duplicate
            ref="modalDuplicateNote"
            @reload="getList"
            :patientId="patientId"
        />
        <modal-edit-diagnosis
            ref="modalEditDiagnosis"
            @onSuccess="$emit('open-diagnosis')"
            :patientId="patientId"
        />
        <modal-record ref="modalRecord" @onSuccess="onSuccess" />
        <modal-cage-card ref="modalCageCard" />
        <modal-view-record ref="modalViewRecord" />
        <modal-view-history ref="modalViewHistory" />
        <modal-add-addon-test ref="modalAddAddonTest" />
    </div>
</template>
<script>
import Records from "@/services/records";
import History from "@/services/patient-history";
import Loader from "@/components/elements/loader-list";
import RowRecord from "@/components/medical-records/record-row";
import AccountService from "@/services/account.js";
import EXTENSIONS from "@/constants/extensions";
import { find } from "lodash";

const PHARMACY = 3;

export default {
    components: {
        Loader,
        RowRecord,
        RowNotes: () => import("@/components/medical-notes/note-row"),
        ModalDelete: () =>
            import("@/components/medical-notes/note-delete-modal"),
        SoapDelete: () => import("@/components/soap/soap-delete"),
        ModalDuplicate: () =>
            import("@/components/medical-notes/note-duplicate-modal"),
        ModalEditDiagnosis: () =>
            import("@/components/commons/modal-diagnosis"),
        ModalDeleteRecord: () =>
            import("@/components/medical-records/record-delete-modal"),
        ModalRecord: () => import("@/module/commons/record/create-update"),
        ModalCageCard: () => import("@/module/schedule/appointment/cage-card"),
        ModalViewRecord: () => import("@/components/medical-records/view"),
        ModalViewHistory: () => import("@/components/medical-records/history"),
        HistoryPagination: () =>
            import("@/components/patient-history-pagination/history-pagination"),
        ModalAddAddonTest: () =>
            import("@/components/patient-lab/add-addon-test-modal")
    },
    props: {
        category: {
            type: [Number, String],
            default: null
        },
        patientDetails: {
            type: Object
        }
    },
    data() {
        return {
            list: [],
            selectedRowId: null,
            isLoading: false,
            meta: null,
            contentExpandedId: null,
            isNewLab: false,
            source: null,
            PHARMACY_ID: PHARMACY
        };
    },
    computed: {
        isSoapEnableBeta() {
            return this.$store.getters["soap/isSoapEnableBeta"];
        },
        isSoap() {
            return this.$route.name === "soap";
        },
        soapDetails() {
            if (this.isSoap) {
                return this.$store.getters["soap/soapDetails"];
            } else {
                return null;
            }
        },
        patientId() {
            return this.patientDetails ? this.patientDetails.id : null;
        },
        count() {
            return this.isSoap
                ? this.$store.state.soap.countsSoap
                : this.$store.state.patient.counts;
        },
        vetsourceEnabled() {
            return this._isExtensionEnabled(EXTENSIONS.CODE.CODE_VETSOURCE);
        },
        vetcoveEnabled() {
            return this._isExtensionEnabled(
                EXTENSIONS.CODE.CODE_VETCOVE_ONLINE
            );
        }
    },
    mounted() {
        this.getList();
        this.isNewLabProfile();

        this.$root.$on("antech-status-update", this.getList);
    },
    beforeDestroy() {
        this.list = [];
        this.meta = null;

        this.$root.$off("antech-status-update", this.getList);
    },
    sockets: {
        "v-broadcast"(payload) {
            if (
                payload.action === "patient-history-refresh" &&
                payload.patientId === this.patientId
            ) {
                this.getList();
            }
        }
    },
    methods: {
        updateInvoiceStatus(recordId, invoiceResponse) {
            let row = find(this.list, { id: recordId });
            row.invoice_item.invoice = {
                ...row.invoice_item.invoice,
                ...invoiceResponse
            };
        },
        expandSummary(id) {
            this.contentExpandedId = id;
        },
        getPatientOwnerId() {
            const { activeOwners } = this.patientDetails;
            if (activeOwners) {
                return activeOwners[0]?.id;
            } else {
                return null;
            }
        },
        viewRecord(record) {
            Records.showRecordDetails(record.id, record.business_id).then(
                response => {
                    if (response) {
                        this.$refs.modalViewRecord.open({
                            ...record,
                            record_resources: response
                        });
                    }
                }
            );
        },
        editRecord(record) {
            this.selectedRowId = record.id;
            let date = this._fromDateCarbon(record.date).toSeconds();
            if (record.invoice_item && record.invoice_item.date) {
                if (
                    this._fromDateCarbon(record.invoice_item.date) <
                    this._fromDateCarbon(record.date)
                ) {
                    date = this._fromDateCarbon(
                        record.invoice_item.date
                    ).toSeconds();
                }
            }

            this.$store.dispatch("checkBillingLock", {
                date,
                onProceed: () => {
                    Records.editRecordDetails(record.id).then(response => {
                        if (response) {
                            const editedRecord = {
                                ...record,
                                clientID: this.getPatientOwnerId(),
                                action: "edit",
                                module: this.MODULE.PATIENT,
                                record_resources: response
                            };
                            if (this.isSoap) {
                                // VET-5725 : From soap record tab trigger open modal from the parent component to avoid closing the modal when changing tab
                                this.$root.$emit("open-medical-record-modal", {
                                    recordParams: editedRecord
                                });
                            } else {
                                this.$refs.modalRecord.open(editedRecord);
                            }
                        }
                        this.selectedRowId = null;
                    });
                },
                onError: message => this._message(message, "danger")
            });
        },
        duplicateRecord(record) {
            this.selectedRowId = record.id;
            Records.duplicateRecordDetails(record.id).then(response => {
                if (response) {
                    if (!response.is_parent_item && response.canDuplicate) {
                        this.$refs.modalRecord.open({
                            ...record,
                            clientID: this.getPatientOwnerId(),
                            action: "duplicate",
                            module: this.MODULE.PATIENT,
                            record_resources: response
                        });
                    } else {
                        this._message(response.message, "danger");
                    }
                }
                this.selectedRowId = null;
            });
        },
        refillRecord(record) {
            this.selectedRowId = record.id;
            const recordId = record.refill_pid ? record.refill_pid : record.id;

            Records.refillRecordDetails(recordId).then(response => {
                if (response) {
                    if (response.canRefill == 1) {
                        this.$refs.modalRecord.open({
                            ...record,
                            clientID: this.getPatientOwnerId(),
                            action: "refill",
                            module: this.MODULE.PATIENT,
                            record_resources: response
                        });
                    } else {
                        this._message(response.message, "danger");
                    }
                }
                this.selectedRowId = null;
            });
        },
        printLabel(record) {
            let recordType = null;

            if (record && record.type) {
                recordType = record.type;
            }

            this.$refs.modalCageCard.open({
                action: "PRINT_LABEL",
                record_id: record.id,
                record_type: recordType // record.item.item_type.id
            });
        },
        getList(page = 1, refreshProfile = false) {
            this.isLoading = true;

            let params = {
                page: page,
                perPage: this.PER_PAGE,
                type: "record",
                category: this.category ? this.category : ""
            };

            if (this.isSoap) {
                params.soapId = this.soapDetails?.id;
            }

            // if category is pharmacy & source is not null, add params.source
            if (this.category == PHARMACY && this.source) {
                params.source = this.source;
            }

            History.getAllHistory(this.patientId, params)
                .then(response => {
                    if (response.message.code == 200) {
                        if (refreshProfile) {
                            this.$emit("refresh-profile");
                        }
                        this.list = response.resources;
                    } else {
                        this.showError();
                    }
                    this.isLoading = false;
                })
                .catch(error => {
                    this.showError();
                    console.error(error);
                });
        },
        showError() {
            this._message(
                "Error : Unable to get medical record list",
                "danger"
            );
            this.isLoading = false;
        },
        deleteRecord(row) {
            let date = row.date
                ? this._fromDateCarbon(row.date).toFormat("X")
                : null;

            this.$store.dispatch("checkBillingLock", {
                date,
                onProceed: () => this.$refs.modalDeleteRecord.open(row),
                onError: message => this._message(message, "danger")
            });
        },
        viewHistoryRecord(row) {
            this.$refs.modalViewHistory.open(row);
        },
        editNote(row) {
            window.location.href = process.env.CI_APPS + row.ci_url;
        },
        deleteNote(row) {
            this.$refs.modalDeleteNote.open(row);
        },
        duplicateNote(row) {
            this.$refs.modalDuplicateNote.open(row);
        },
        editDiagnosis(diagnosis) {
            let self = this;
            Records.getDiagnosisDetails(this.patientId, diagnosis.id).then(
                response => {
                    if (response.message.code === 200) {
                        self.$refs.modalEditDiagnosis.open({
                            action: "edit",
                            editRecord: response.resource
                        });
                    }
                }
            );
        },
        onSuccess(refreshProfile = false) {
            if (this.isSoap) {
                // for soap, it will emit the main file which is soap.vue to refresh list and count
                this.$emit("onSuccess");
            } else {
                this.$store.dispatch("patient/getHistoryCount", {
                    patientId: this.patientId
                });
                this.getList(1, refreshProfile);
            }
        },
        async isNewLabProfile() {
            let res = await AccountService.checkFeature("lab_profile");
            let isEnabled = res?.message?.code;

            if (isEnabled === 404) {
                this.isNewLab = true;
            }
        },
        reloadTabCount() {
            this.$emit("reload-tab-count");
        },
        addAddonTest(row) {
            this.$refs.modalAddAddonTest.open({
                lab_pid: row.lab_pid,
                id: row.id
            });
        }
    }
};
</script>
